"use client";

import { useEffect, useMemo } from "react";

import { SSR_CODES, SSR_CODES_CHECKIN } from "@repo/ui/constants/ssrs";
import type { UserRole } from "@repo/ui/constants/userRoles";
import { findSSRWithPurchasePriority } from "@repo/ui/helpers/Extras/findPurchasedSsr";
import { isSsrIncluded } from "@repo/ui/helpers/Extras/isSsrIncluded";
import { calculatePetOnBoardPrice } from "@repo/ui/helpers/petPricing";
import type { PassengerSegment } from "@repo/ui/types/booking";
import type { CommonInfoSection, PetOnBoardSection } from "@repo/ui/types/cms";
import type { Passenger } from "@repo/ui/types/manageBooking";

import { useAppDispatch } from "@repo/store/hooks";
import { setPetOnBoardButtonStatus, useIsCheckin } from "@repo/store/slices";

import FlightSegmentCard from "../common/FlightSegmentCard";
import { PassengerItemCard } from "../common/PassengerItemCard";

type PetOnBoardOptionCardProps = {
  direction: string;
  directionLabel?: string;
  origin: string;
  destination: string;
  options: Record<string, PassengerSegment>;
  onChangeAction: (passengerKey: string, price: string | number, ssrKey?: string) => void;
  addedPets: Record<string, boolean>;
  bundleCode?: string;
  disabled?: boolean;
  maxSelections?: number;
  remainingPets?: number;
  InBundleInAnyJourney?: boolean;
  commonInfo?: CommonInfoSection;
  cmsData?: PetOnBoardSection;
  firstPassengerKeys?: Record<string, string>;
  userType?: UserRole;
  passengers?: Passenger[];
  isJourneyDisabled?: boolean;
  loadingKeys?: string[];
};

export function ExtrasPetOnBoardOptionCard({
  direction,
  directionLabel,
  options,
  addedPets,
  onChangeAction,
  bundleCode,
  disabled,
  maxSelections = 4,
  remainingPets,
  InBundleInAnyJourney,
  commonInfo,
  cmsData,
  firstPassengerKeys,
  userType,
  passengers,
  isJourneyDisabled = false,
  loadingKeys,
}: Readonly<PetOnBoardOptionCardProps>) {
  const dispatch = useAppDispatch();
  const isCheckIn = useIsCheckin();

  const petOnBoardCode = useMemo<string>(() => {
    return isCheckIn ? SSR_CODES_CHECKIN.PET_ON_BOARD : SSR_CODES.PET_ON_BOARD;
  }, [isCheckIn]);

  const petOnBoardCodes = useMemo<readonly string[]>(() => {
    return isCheckIn
      ? [SSR_CODES_CHECKIN.PET_ON_BOARD, SSR_CODES.PET_ON_BOARD]
      : [SSR_CODES.PET_ON_BOARD];
  }, [isCheckIn]);

  useEffect(() => {
    Object.values(options).forEach((option) => {
      const optionKey = `${option.passengerKey}_${direction}`;
      if (addedPets[optionKey]) return;

      const isPending = (loadingKeys ?? []).some((key) => key.startsWith(`${optionKey}_`));
      if (isPending) return;

      const petOnBoardSSR = findSSRWithPurchasePriority(
        option.ssrs,
        petOnBoardCodes,
        petOnBoardCode,
      );
      const priceSSR =
        option.ssrs.find(
          (s) => petOnBoardCodes.includes(s.ssrCode) && !s.isConfirmed && !s.inBundle,
        ) || petOnBoardSSR;
      const basePrice = priceSSR?.price ? Number(priceSSR.price) : 0;

      const isSSRAvailable = !!petOnBoardSSR?.ssrKey;

      const finalPrice = calculatePetOnBoardPrice(
        userType as UserRole,
        option.passengerKey,
        firstPassengerKeys ?? {},
        direction,
        basePrice,
        isSSRAvailable,
      );

      const isIncluded = petOnBoardSSR?.inBundle || petOnBoardSSR?.isConfirmed;

      if (isIncluded && petOnBoardSSR?.ssrKey) {
        dispatch(
          setPetOnBoardButtonStatus({
            isAdded: true,
            price: finalPrice,
            direction,
            passengerId: option.passengerKey,
            cartType: isCheckIn ? "checkInCart" : "bookingCart",
            alreadyAdded: true,
            ssrKey: petOnBoardSSR.ssrKey,
            ssrCode: petOnBoardSSR.ssrCode,
          }),
        );
      }
    });
  }, [
    dispatch,
    options,
    direction,
    firstPassengerKeys,
    userType,
    isCheckIn,
    petOnBoardCode,
    petOnBoardCodes,
    addedPets,
    loadingKeys,
  ]);

  const selectedCountInDirection = useMemo(
    () =>
      Object.entries(addedPets).filter(([key, value]) => value && key.endsWith(`_${direction}`))
        .length,
    [addedPets, direction],
  );

  const previouslyPurchasedCount = useMemo(
    () =>
      Object.values(options).filter((option) => {
        const petOnBoardSSR = findSSRWithPurchasePriority(
          option.ssrs,
          petOnBoardCodes,
          petOnBoardCode,
        );
        const isIncluded =
          petOnBoardSSR?.inBundle ||
          petOnBoardSSR?.isConfirmed ||
          petOnBoardSSR?.isConfirmingUnheld;
        const includedInPurchase = isSsrIncluded(
          passengers,
          option.passengerKey,
          SSR_CODES.PET_ON_BOARD,
          direction,
        );
        return isIncluded || includedInPurchase;
      }).length,
    [options, petOnBoardCodes, petOnBoardCode, passengers, direction],
  );

  const pendingAddCount = useMemo(
    () =>
      (loadingKeys || []).filter((key) => {
        if (!key.endsWith(`_${direction}_add`)) return false;
        const originalKey = key.replace("_add", ""); // e.g. "ADT_1_OUTBOUND"
        return !addedPets[originalKey]; // Only count if Redux hasn't caught up yet
      }).length,
    [loadingKeys, direction, addedPets],
  );

  const pendingRemoveCount = useMemo(
    () =>
      (loadingKeys || []).filter((key) => {
        if (!key.endsWith(`_${direction}_remove`)) return false;
        const originalKey = key.replace("_remove", "");
        return addedPets[originalKey]; // Only count if Redux hasn't caught up yet
      }).length,
    [loadingKeys, direction, addedPets],
  );

  const totalAddedCount = selectedCountInDirection + pendingAddCount - pendingRemoveCount;

  const newlyAddedCount = Math.max(0, totalAddedCount - previouslyPurchasedCount);

  return (
    <FlightSegmentCard
      bundleCode={bundleCode}
      direction={direction}
      directionLabel={directionLabel}
      disabled={disabled || isJourneyDisabled}
      InBundleInAnyJourney={InBundleInAnyJourney}
    >
      {Object.values(options).map((option, index) => {
        const petOnBoardSSR = findSSRWithPurchasePriority(
          option.ssrs,
          petOnBoardCodes,
          petOnBoardCode,
        );

        const includedInPurchase = isSsrIncluded(
          passengers,
          option.passengerKey,
          SSR_CODES.PET_ON_BOARD,
          direction,
        );

        const isSSRAvailable = !!petOnBoardSSR?.ssrKey;

        const isIncluded = petOnBoardSSR?.inBundle || petOnBoardSSR?.isConfirmed;

        const optionKey = `${option.passengerKey}_${direction}`;
        const isOptionSelected = !!addedPets[optionKey];
        const isOptionPendingAdd = !!loadingKeys?.includes(`${optionKey}_add`);
        const isEffectivelySelected = isOptionSelected || isOptionPendingAdd;

        const isDynamicCapacityFull =
          remainingPets !== undefined && remainingPets - newlyAddedCount <= 0;
        const isMaxSelected = totalAddedCount >= maxSelections;
        const ssrKey = petOnBoardSSR?.ssrKey;
        const isDisabled =
          ((isMaxSelected || isDynamicCapacityFull) && !isEffectivelySelected) ||
          !!disabled ||
          isIncluded ||
          includedInPurchase ||
          isJourneyDisabled ||
          !ssrKey;

        const isSoldOut =
          (!ssrKey && !isIncluded && !includedInPurchase) ||
          ((isMaxSelected || isDynamicCapacityFull) && !isEffectivelySelected);

        const priceSSR =
          option.ssrs.find(
            (s) => petOnBoardCodes.includes(s.ssrCode) && !s.isConfirmed && !s.inBundle,
          ) || petOnBoardSSR;

        const finalPrice = calculatePetOnBoardPrice(
          userType as UserRole,
          option.passengerKey,
          firstPassengerKeys ?? {},
          direction,
          priceSSR?.price ? Number(priceSSR.price) : 0,
          isSSRAvailable,
          isSoldOut,
        );

        return (
          <PassengerItemCard
            key={option.passengerKey}
            index={index}
            passengerKey={option.passengerKey}
            isAdded={!!addedPets[optionKey]}
            price={finalPrice}
            disabled={isDisabled}
            onChangeAction={onChangeAction}
            cmsData={cmsData}
            commonInfo={commonInfo}
            ssrKey={ssrKey}
            loading={
              loadingKeys?.includes(`${option.passengerKey}_${direction}_add`) ||
              loadingKeys?.includes(`${option.passengerKey}_${direction}_remove`)
            }
            isSoldOut={isSoldOut}
          />
        );
      })}
    </FlightSegmentCard>
  );
}
